import type { AttachmentIn, LifeFileWithRelationDTO, RelationDTO } from '../memorize/memorize'
import type { FileDTO } from '../media/media'
import {
  ALBUM_ADD_FILE_PRE,
  ALBUM_CREATE,
  ALBUM_FILE,
  ALBUM_FILE_LIST,
  ALBUM_FLLE_ADD,
  ALBUM_LIST,
  ALBUM_MODIFY,
  ALBUM_PATH,
  ALBUM_RENAME,
  ALBUM_SET_FOLDER,
  ALBUM_SET_YEAR,
  ALBUM_SORT_FILE,
  ALBUM_UPLOAD,
  CommonResponse,
  netClient
} from '@/apis/api'
import { CODE_OK } from '@/apis/code'
import { defineStore } from 'pinia'
import { computed, ref, type Ref } from 'vue'

export type AlbumType = 'waterfall' | 'album' | 'timeline'

export const FOLDER_TYPE_NONE = 0
export const FOLDER_TYPE_COMMON = 1
export const FOLDER_TYPE_YEAR = 2

export const FOLDER_NAME_DEFAULT = '_default'

export interface AlbumFolderDTO {
  id: string
  folderName: string
  folderDesc: string
  year: string
  folderType: number
  creatorId: string
  serviceId: string
  lifeId: string
  createdAt: number
  updatedAt: number
  files: Array<LifeFileWithRelationDTO>
  page: number
  total: number
  pageSize: number
}

export interface PreAddAlbumFileResponse {
  commonFolders: Array<AlbumFolderDTO>
  yearFolders: Array<AlbumFolderDTO>
  relation: RelationDTO
}

export interface CreateAlbumCommand {
  albumName: string
  albumDesc: string
  folderType: number
  year: string
  serverId: string
}

export interface RenameAlbumFolderCommand {
  folderId: string
  folderName: string
}

export interface ModifyAlbumDescCommand {
  folderId: string
  folderName: string
  folderDesc: string
}

export interface AddAlbumFileCommand {
  folderId: string
  fileId: string
}

export async function createAlbum(command: CreateAlbumCommand) {
  return netClient.request<CommonResponse<AlbumFolderDTO>>(ALBUM_CREATE, 'post', command)
}

export async function modifyFolderDesc(command: ModifyAlbumDescCommand) {
  return netClient.request<CommonResponse<AlbumFolderDTO>>(ALBUM_MODIFY, 'post', command)
}

export async function listAlbumFolders(serviceId: string, pageSize: number, type: number) {
  return netClient.request<CommonResponse<ListAlbumFoldersResponse>>(ALBUM_LIST, 'get', {
    serviceId: serviceId,
    pageSize: pageSize,
    type: type
  })
}

export async function renameAlbum(command: RenameAlbumFolderCommand) {
  return netClient.request<CommonResponse<AlbumFolderDTO>>(ALBUM_RENAME, 'post', command)
}

export async function deleteAlbum(folderId: string) {
  return netClient.request<CommonResponse<AlbumFolderDTO>>(ALBUM_PATH.replace('{folderId}', folderId), 'delete', {})
}

export async function addAlbumFile(command: AddAlbumFileCommand) {
  return netClient.request<CommonResponse<AlbumFolderDTO>>(ALBUM_FLLE_ADD, 'post', command)
}

export async function deleteAlbumFile(fileId: string) {
  return netClient.request<CommonResponse<any>>(ALBUM_FILE + '/file/' + fileId, 'delete', {})
}

export async function preAddAlbumFile(serviceId: string) {
  return netClient.request<CommonResponse<PreAddAlbumFileResponse>>(ALBUM_ADD_FILE_PRE, 'get', {
    serviceId: serviceId
  })
}

export async function uploadAlbumFiles(command: UploadAlbumFileCommand) {
  return netClient.request<CommonResponse<any>>(ALBUM_UPLOAD, 'post', command)
}

// store for album folders
type FolderStore = {
  commonFolders: AlbumFolderDTO[]
  yearFolders: AlbumFolderDTO[]
}

interface FolderState {
  folders: Record<string, FolderStore>
}

export interface UploadAlbumFileCommand {
  type: string
  attachments: Array<AttachmentIn>
  relationName: string
  relationCode: string
  serviceId: string
}

export interface SetFolderCommand {
  fileId: string
  albumFolderIds: Array<String>
  serviceId: string
}

export interface SetYearCommand {
  fileId: string
  year: string
  serviceId: string
}

export interface FileSortPair {
  left: string
  right: number
}

export async function setFileFolder(command: SetFolderCommand) {
  return netClient.request<CommonResponse<any>>(ALBUM_SET_FOLDER, 'post', command)
}

export async function setYearFolder(command: SetYearCommand) {
  return netClient.request<CommonResponse<any>>(ALBUM_SET_YEAR, 'post', command)
}

export interface SortAlbumFileCommand {
  sortPairs: Array<FileSortPair>
}

export async function sortAlbumFiles(command: SortAlbumFileCommand) {
  return netClient.request<CommonResponse<any>>(ALBUM_SORT_FILE, 'post', command)
}

export interface ListAlbumFoldersResponse {
  folders: Array<AlbumFolderDTO>
}

export interface ListFilesInFolderResponse {
  files: Array<LifeFileWithRelationDTO>
  page: number
  total: number
  pageSize: number
}

export function getAlbumFileByPage(folderId: string, page: number, pageSize: number) {
  return netClient.request<CommonResponse<ListFilesInFolderResponse>>(ALBUM_FILE_LIST, 'get', {
    folderId: folderId,
    pageSize: pageSize,
    page: page
  })
}

// delete album folder
export function deleteAlbumFolder(folderId: string) {
  return netClient.request<CommonResponse<AlbumFolderDTO>>(ALBUM_PATH.replace('{folderId}', folderId), 'delete', {})
}

// storage
export const useAlbumFolderStore = defineStore('albumFolders', () => {
  const albumFolderType: Ref<number> = ref(-1)

  const setAlbumFolderType = (type: number) => {
    albumFolderType.value = type
  }

  const clearAlbumFolderType = () => {
    albumFolderType.value = -1
  }

  const folders = ref<Record<string, FolderStore>>({})
  const fetchingServiceIds = ref<string[]>([])

  // 初始化服务存储
  const initService = (serviceId: string) => {
    if (!folders.value[serviceId]) {
      folders.value[serviceId] = {
        commonFolders: [],
        yearFolders: []
      }
    }
  }

  // 操作公共文件夹
  const addCommonFolder = (serviceId: string, folder: AlbumFolderDTO) => {
    initService(serviceId)
    folders.value[serviceId].commonFolders.push(folder)
  }

  const setCommonFolder = (serviceId: string, newFolders: AlbumFolderDTO[]) => {
    initService(serviceId)
    folders.value[serviceId].commonFolders = newFolders
  }

  const setYearFolder = (serviceId: string, newFolders: AlbumFolderDTO[]) => {
    initService(serviceId)
    folders.value[serviceId].yearFolders = newFolders
  }

  // 操作年度文件夹
  const addYearFolder = (serviceId: string, folder: AlbumFolderDTO) => {
    initService(serviceId)
    folders.value[serviceId].yearFolders.push(folder)
  }

  // 删除文件夹
  const deleteFolder = (serviceId: string, folderId: string) => {
    initService(serviceId)
    const service = folders.value[serviceId]

    service.commonFolders = service.commonFolders.filter((f) => f.id !== folderId)
    service.yearFolders = service.yearFolders.filter((f) => f.id !== folderId)
  }

  // 更新文件夹
  const updateFolder = (serviceId: string, folder: AlbumFolderDTO) => {
    initService(serviceId)
    const service = folders.value[serviceId]

    const updateList = (list: AlbumFolderDTO[]) => list.map((f) => (f.id === folder.id ? folder : f))

    service.commonFolders = updateList(service.commonFolders)
    service.yearFolders = updateList(service.yearFolders)
  }

  // 计算属性
  const getServiceFolders = computed(() => (serviceId: string) => {
    return folders.value[serviceId] || { commonFolders: [], yearFolders: [] }
  })

  const getAllFolders = computed(() => (serviceId: string) => {
    const service = folders.value[serviceId]
    return service ? [...service.commonFolders, ...service.yearFolders] : []
  })

  const ensureServiceFolders = async (serviceId: string, force: boolean = false) => {
    if (!serviceId) {
      return
    }
    initService(serviceId)
    const service = folders.value[serviceId]
    if (!force && (service.commonFolders.length > 0 || service.yearFolders.length > 0)) {
      return
    }
    if (fetchingServiceIds.value.includes(serviceId)) {
      return
    }
    fetchingServiceIds.value.push(serviceId)
    try {
      const { data, error } = await preAddAlbumFile(serviceId)
      if (error.value) {
        return
      }
      const resData = data as Ref<CommonResponse<PreAddAlbumFileResponse>>
      if (resData.value?.code === CODE_OK && resData.value?.data) {
        setCommonFolder(serviceId, resData.value.data.commonFolders || [])
        setYearFolder(serviceId, resData.value.data.yearFolders || [])
      }
    } finally {
      fetchingServiceIds.value = fetchingServiceIds.value.filter((id) => id !== serviceId)
    }
  }

  return {
    albumFolderType,
    setAlbumFolderType,
    clearAlbumFolderType,
    folders,
    initService,
    addCommonFolder,
    setCommonFolder,
    setYearFolder,
    addYearFolder,
    deleteFolder,
    updateFolder,
    getServiceFolders,
    getAllFolders,
    ensureServiceFolders
  }
})
